/* eslint-disable no-loop-func */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ReactComponent as IconMail } from 'static/svg/icon_mail_24x24.svg'
import { ReactComponent as IconPhone } from 'static/svg/icon_telefone_24x24.svg'

import * as actions from 'redux/actions'

import Modal from '../Modal'
import { Avatar, Typography, Spinner } from '@telavita-core/react-design-kit'

import { PlanGroupCategoryEnum } from 'settings/_planGroupSettings'

import { getCorporateEligibility } from 'services/corporateEligibilityService'
import { getLastValidityDetail } from 'services/lastValidityDetail'

import { cellphoneMask } from 'utils'
import { getTreatmentStatus } from 'services/treatments'

import PopoverMenu from 'components/PopoverMenu'
import EnterpriseEligibility from './components/EnterpriseEligibility/EnterpriseEligibility'
import CorporateEligibility from './components/CorporateEligibility/CorporateEligibility'
import { INACTIVE } from 'settings/_personSettings'

const PatientDetail = ({
  patientData: {
    full_name,
    email,
    cellphone_number,
    date_created,
    username,
    plans
  },
}) => {

  const authUserPlanGroupCode = useSelector(state => state?.auth?.planSelected?.plan_group?.code)

  const [tab, setTab] = useState('ACTIVE')
  const [loadingPlansEligibility, setLoadingPlansEligibility] = useState(true)

  const [activePlansEligibility, setActivePlansEligibility] = useState([])
  const [inactivePlansEligibility, setInactivePlansEligibility] = useState([])

  const dispatch = useDispatch()

  const {
    CARE_PLUS,
    CORPORATE_LIMITED,
    CORPORATE_UNLIMITED,
    ENTERPRISE,
    VITTA,
    CAPITATION
  } = PlanGroupCategoryEnum

  const fetchPlansEligibility = async (plans) => {
    if (plans.length <= 0) return

    setLoadingPlansEligibility(true)

    let plansEligibility = []

    const lastPlan = plans.slice(-1)[0]

    for await (const plan of plans) {
      const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(
        plan.plan.group?.category
      )

      const isEnterprise = ([CARE_PLUS, ENTERPRISE, CAPITATION]).includes(plan.plan.group?.category)

      const psychiatryEligibility = await getTreatmentStatus({
        userName: username,
        consultationType: 'PSIQ',
        planCode: plan.plan.code
      })

      const showSpecialities = plan.plan.psychiatry_enabled

      if (isCorporate) {
        await getCorporateEligibility({
          username: email,
          planCode: plan.plan.code,
        }).then((eligibility) => {
          plansEligibility = [...plansEligibility, {
            ...plan,
            eligibility,
            psychiatry: psychiatryEligibility,
            isCorporate: true,
            plan_group: {
              ...plan.plan.group,
            },
            showSpecialities: showSpecialities
          }]
        })
      } else if (isEnterprise) {
        
        await getLastValidityDetail(email, plan.plan.code).then(
          (validityDetail) => {
            plansEligibility = [...plansEligibility, {
              ...plan,
              eligibility: validityDetail,
              psychiatry: psychiatryEligibility,
              showSpecialities: showSpecialities
            },]
          }
        ).catch(err => {
          plansEligibility = [...plansEligibility, {
            ...plan,
            isActive: plan.status_code !== INACTIVE,
            eligibility: {},
            psychiatry: psychiatryEligibility,
            showSpecialities: showSpecialities
          },]
        })
      } else {
        plansEligibility = [...plansEligibility, plan]
      }

      if (plan.plan.code === lastPlan.plan.code) {
        setLoadingPlansEligibility(false)
      }

    }
    return plansEligibility
  }

  const getActivePlansEligibility = async () => {
    const activePlans = plans.filter(plan => plan.plan.group.code === authUserPlanGroupCode && plan.plan.status_code !== INACTIVE)

    if (activePlans.length <= 0) return

    const data = await fetchPlansEligibility(activePlans)

    const hasMoreThenOnePlan = data.length > 2

    const plansFilteredWithoutParticular = hasMoreThenOnePlan
      ? data.filter(plan => !plan.is_particular)
      : data

    setActivePlansEligibility(plansFilteredWithoutParticular)

    setLoadingPlansEligibility(false)
  }

  const getInactivePlansEligibility = async () => {
    const inactivePlans = plans.filter(plan => plan.plan.group.code === authUserPlanGroupCode && plan.plan.status_code === INACTIVE)

    if (inactivePlans.length <= 0) return

    const data = await fetchPlansEligibility(inactivePlans)

    setInactivePlansEligibility(data)

    setLoadingPlansEligibility(false)
  }

  useEffect(() => {
    getActivePlansEligibility()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans])

  const closeModal = () => {
    dispatch(actions.hideModal())
  }

  return (
    <Modal customClassName='PatientDetailModal' btnText='Fechar' handleClick={() => closeModal()} hasButton={true}>
      <div className='ContainerPatientDetail'>
        <div className='ContainerPatientDetail__header'>
          <Avatar alt='paciente' src='/static/avatar.png' size='large' />
          <div className='ContainerPatientDetail__header__userData'>
            <span>Paciente</span>
            <Typography weight='bold' variant='header4'>
              {full_name}
            </Typography>
            <ul className='ContainerPatientDetail__header__userData__list'>
              <li>
                <IconPhone />
                <Typography variant='content2'>
                  {cellphoneMask(cellphone_number)}
                </Typography>
              </li>
              <li>
                <IconMail />
                <Typography variant='content2'>{email}</Typography>
              </li>
            </ul>
          </div>
        </div>

        {loadingPlansEligibility ? (
          <Spinner inlineStyles={{ margin: '0 auto' }} />
        ) : (
          <>
            {(
              <div className='ContainerPatientDetail__statusSelector'>
                <Typography weight='bold' variant='content2'>
                Convênios
                </Typography>
                <PopoverMenu
                  label='VISUALIZAR CONVÊNIOS:'
                  options={[
                    {
                      id: 'ACTIVE',
                      title: 'Ativos',
                      onClick: () => setTab('ACTIVE'),
                    },
                    {
                      id: 'INACTIVE',
                      title: 'Inativos',
                      onClick: () => {
                        setTab('INACTIVE')
                        if (inactivePlansEligibility.length <= 0) {
                          getInactivePlansEligibility()
                        }
                      },
                    },
                  ]}
                  selectedOption={tab}
                  buttonText={tab === 'ACTIVE' ? 'Ativos' : 'Inativos'}
                />
              </div>
            )}
            {tab === 'ACTIVE' && (  
              <>
                {activePlansEligibility.map((plan) => {
                  if (plan?.is_particular) {
                    return (
                      <Typography weight='bolder' variant='content1'>
                        Particular (sem convênio)
                      </Typography>
                    )
                  }
                  if (plan?.plan.group.category === VITTA) {
                    return (
                      <Typography weight='bolder' variant='content1' inlineStyles={{ marginBottom: 24 }}>
                        Convênio {plan.plan.group.name} {plan.plan.name} 
                      </Typography>
                    )
                  }
                  if (plan?.isCorporate) {
                    return (
                      <CorporateEligibility 
                        key={plan.plan.code}
                        availableCredits={plan.eligibility.availableCredits}
                        dateCreated={date_created}
                        expirationDate={plan.eligibility.expirationDate}
                        frequency={plan.eligibility.frequency}
                        maxCredits={plan.eligibility.maxCredits}
                        isEligible={plan.eligibility.isEligible}
                        isCorporateLimited={plan.plan.group.category === CORPORATE_LIMITED}
                        planName={plan.plan.name}
                        planGroupName={plan.plan.group.name}
                        cycleDate={plan?.psychiatry?.cycleDate}
                        cycleStatus={plan?.psychiatry?.cycleStatus}
                        showSpecialties={plan?.showSpecialities}
                        isActive
                      />
                    )
                  } else {
                    return (
                      <EnterpriseEligibility
                        key={plan.plan_code}
                        availableCredits={plan.eligibility.availableCredits}
                        maxAppointment={plan.eligibility.maxAppointment}
                        cardNumber={plan.card_number}
                        dateCreated={plan.eligibility.dateCreated}
                        endDate={plan.eligibility.endDate}
                        status={plan.eligibility.status}
                        isValidatedOrFinished={[
                          'VALIDATED',
                          'FINISHED',
                          'VALIDATED_WITHOUT_MEDICAL_REQUEST',
                        ].includes(plan.eligibility.status)}
                        isWaitingOrDisapproved={[
                          'WAITING_TECHNICAL_EVALUATION',
                          'WAITING_VALIDATION',
                          'DISAPPROVED',
                          'INVALIDATED',
                        ].includes(plan.eligibility.status)}
                        planName={plan.plan.name}
                        planGroupName={plan.plan.group.name}
                        isActive
                        cycleDate={plan?.psychiatry?.cycleDate}
                        cycleStatus={plan?.psychiatry?.cycleStatus}
                        showSpecialities={plan?.showSpecialities}
                        treatmentNotStarted={Object.keys(plan.eligibility).length <= 0}
                      />
                    )
                  }
                })}
              </>
            )}
            {tab === 'INACTIVE' && (
              <>
                {inactivePlansEligibility.length > 0 ? (
                  <>
                {inactivePlansEligibility.map((plan) => {
                  if (plan?.isCorporate) {
                    return (
                      <CorporateEligibility 
                        key={plan.plan.code}
                        availableCredits={plan.eligibility.availableCredits}
                        dateCreated={date_created}
                        expirationDate={plan.eligibility.expirationDate}
                        frequency={plan.eligibility.frequency}
                        maxCredits={plan.eligibility.maxCredits}
                        isEligible={plan.eligibility.isEligible}
                        isCorporateLimited={plan.plan.group.category === CORPORATE_LIMITED}
                        planName={plan.plan.name}
                        planGroupName={plan.plan.group.name}
                        inactivatePlanInfo={{
                          inactivatedAt: plan.inactivated_at,
                          inactivatedBy: plan.inactivated_by,
                          reason: plan.reason,
                          moreInfo: plan.more_info
                        }}
                      />
                    )
                  } else {
                    return (
                      <EnterpriseEligibility
                        key={plan.plan.code}
                        availableCredits={plan.eligibility.availableCredits}
                        maxAppointment={plan.eligibility.maxAppointment}
                        cardNumber={plan.card_number}
                        dateCreated={plan.eligibility.dateCreated}
                        endDate={plan.eligibility.endDate}
                        status={plan.eligibility.status}
                        isValidatedOrFinished={[
                          'VALIDATED',
                          'FINISHED',
                          'VALIDATED_WITHOUT_MEDICAL_REQUEST',
                        ].includes(plan.eligibility.status)}
                        isWaitingOrDisapproved={[
                          'WAITING_TECHNICAL_EVALUATION',
                          'WAITING_VALIDATION',
                          'DISAPPROVED',
                          'INVALIDATED',
                        ].includes(plan.eligibility.status)}
                        planName={plan.plan.name}
                        planGroupName={plan.plan.group.name}
                        cycleDate={plan?.psychiatry?.cycleDate}
                        cycleStatus={plan?.psychiatry?.cycleStatus}
                        showSpecialities={plan?.showSpecialities}
                        hasTreatment={plan?.eligibility !== null}
                        inactivatePlanInfo={{
                          inactivatedAt: plan.inactivated_at,
                          inactivatedBy: plan.inactivated_by,
                          reason: plan.reason,
                          moreInfo: plan.more_info
                        }}
                      />
                    )
                  }
                })}
                  </>
                ) : (
                  <div className='ContainerPatientDetail__InactiveContainer'>
                    <Typography variant='content3' style={{ color: '#898989' }}>
                    Nenhum convênio inativo
                    </Typography>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  )
}

export default PatientDetail
